import { useState, useRef } from "react";
import axios from "axios";
import { Phone, Mail, Globe, MapPin, User, Download, Send, Loader2, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { CONTACT, CAP_STATEMENT } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InfoRow = ({ icon: Icon, label, value, href, testid }) => (
  <a
    href={href}
    data-testid={testid}
    target={href?.startsWith("http") ? "_blank" : undefined}
    rel="noreferrer"
    className="group flex items-start gap-4 border border-white/10 bg-[#121212] p-5 hover:border-white/40 transition-all"
  >
    <span className="shrink-0 h-11 w-11 grid place-items-center border border-white/15 group-hover:bg-white group-hover:text-black text-white transition-all">
      <Icon size={18} strokeWidth={1.8} />
    </span>
    <span className="min-w-0">
      <span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-1">{label}</span>
      <span className="block font-body text-white text-sm md:text-base break-words">{value}</span>
    </span>
  </a>
);

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", organization: "", solicitation: "", message: "" });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error("File is too large (10MB max).");
      return;
    }
    setFile(f);
  };

  const clearFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      if (file) data.append("attachment", file);
      await axios.post(`${API}/quote`, data, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Request sent — we'll be in touch shortly.");
      setForm({ name: "", email: "", organization: "", solicitation: "", message: "" });
      clearFile();
    } catch (err) {
      toast.error("Could not send your request. Please email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inputCls =
    "bg-[#0A0A0A] border border-white/20 px-4 py-3 text-white placeholder:text-neutral-600 focus:border-white focus:ring-1 focus:ring-white outline-none transition-all w-full font-body text-sm";

  return (
    <section id="contact" data-testid="contact-section" className="relative border-t border-white/10 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left: info */}
          <div>
            <div className="flex items-center gap-4 mb-5">
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-neutral-500">06</span>
              <span className="h-px w-10 bg-white/20" />
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-neutral-400">Contact</span>
            </div>
            <h2 className="font-sans font-black uppercase tracking-tight text-4xl md:text-5xl lg:text-6xl text-white leading-[0.95]">
              Let's Discuss Your Requirement
            </h2>
            <p className="mt-6 font-body text-neutral-400 text-base md:text-lg leading-relaxed max-w-md">
              Reach out directly or send a quote request. We respond to federal agencies and approved prime-contractor partners.
            </p>

            <div className="mt-10 space-y-3">
              <InfoRow icon={User} label="Point of Contact" value={`${CONTACT.name} — ${CONTACT.title}`} testid="contact-poc" />
              <InfoRow icon={Phone} label="Phone" value={CONTACT.phone} href={CONTACT.phoneHref} testid="contact-phone" />
              <InfoRow icon={Mail} label="Email" value={CONTACT.email} href={`mailto:${CONTACT.email}`} testid="contact-email" />
              <InfoRow icon={Globe} label="Website" value={CONTACT.website} href={`https://${CONTACT.website}`} testid="contact-website" />
              <InfoRow icon={MapPin} label="Location" value={CONTACT.location} testid="contact-location" />
            </div>

            <a
              href={CAP_STATEMENT}
              download
              data-testid="contact-download-btn"
              className="mt-6 inline-flex items-center gap-2 border-2 border-white/25 text-white font-sans font-bold uppercase tracking-widest px-7 py-3.5 hover:border-white hover:bg-white/5 transition-all"
            >
              <Download size={18} /> Download Capability Statement
            </a>
          </div>

          {/* Right: form */}
          <div className="border border-white/10 bg-[#121212] p-7 md:p-10">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-neutral-500 mb-2">Request a Quote</p>
            <h3 className="font-sans font-bold uppercase tracking-tight text-3xl text-white mb-8">Send Us The Details</h3>
            <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-4">
              <div>
                <label className="block font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Full Name *</label>
                <input data-testid="form-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Jane Doe" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Email *</label>
                  <input data-testid="form-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@agency.gov" />
                </div>
                <div>
                  <label className="block font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Organization</label>
                  <input data-testid="form-organization" className={inputCls} value={form.organization} onChange={set("organization")} placeholder="Agency / Prime" />
                </div>
              </div>
              <div>
                <label className="block font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Solicitation / RFQ Number</label>
                <input data-testid="form-solicitation" className={inputCls} value={form.solicitation} onChange={set("solicitation")} placeholder="e.g. SPE1C1-25-R-0001 (optional)" />
              </div>
              <div>
                <label className="block font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Requirement Details *</label>
                <textarea data-testid="form-message" rows={5} className={inputCls} value={form.message} onChange={set("message")} placeholder="Describe the products, quantities, NAICS, or scope you need..." />
              </div>
              <div>
                <label className="block font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Attach RFQ / Spec Sheet</label>
                <input
                  ref={fileInputRef}
                  data-testid="form-attachment"
                  type="file"
                  onChange={onFile}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg"
                  className="hidden"
                />
                {!file ? (
                  <button
                    type="button"
                    data-testid="form-attachment-trigger"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full inline-flex items-center justify-center gap-2 border border-dashed border-white/25 text-neutral-400 hover:text-white hover:border-white/50 px-4 py-3.5 font-body text-sm transition-all"
                  >
                    <Paperclip size={16} /> Choose a file (PDF, DOC, XLS — max 10MB)
                  </button>
                ) : (
                  <div data-testid="form-attachment-selected" className="w-full flex items-center justify-between gap-3 border border-white/20 bg-[#0A0A0A] px-4 py-3">
                    <span className="flex items-center gap-2 min-w-0 text-white font-body text-sm">
                      <Paperclip size={15} className="shrink-0" />
                      <span className="truncate">{file.name}</span>
                    </span>
                    <button type="button" data-testid="form-attachment-clear" onClick={clearFile} className="shrink-0 text-neutral-400 hover:text-white" aria-label="Remove file">
                      <X size={16} />
                    </button>
                  </div>
                )}
              </div>
              <button
                type="submit"
                data-testid="form-submit"
                disabled={loading}
                className="group w-full inline-flex items-center justify-center gap-2 bg-white text-black font-sans font-bold uppercase tracking-widest px-8 py-4 hover:bg-neutral-300 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>Sending <Loader2 size={17} className="animate-spin" /></>
                ) : (
                  <>Submit Request <Send size={17} className="group-hover:translate-x-1 transition-transform" /></>
                )}
              </button>
              <p className="font-body text-xs text-neutral-600 text-center">Your request is sent directly to the Atlas Contracting Group team.</p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
